import { useEffect, useState, createContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchDatasources } from '../services/entities/datasource';
import { menuOptions } from './components/Drawer';
import { addPartnerHistory, fetchPartnerHistory } from '../services/entities/search-history';
import { fetchOptions } from '../services/entities/options';
import { fetchAnalytics } from '../services/entities/analytics';
import { fetchLeaderboard } from '../services/entities/leaderboard';
import * as dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { fetchPartnerLogSuggestions } from '../services/entities/partner-logs';
import queryString from 'query-string';

const queryFields = [
  'single_date',
  'date_before',
  'date_after',
  'time_before',
  'time_after',
  'region',
  'base_url',
  'cat_or_uncat',
  'association_type',
  'partner'
];

const baseFiltersFields = ['association_type', 'partner', 'single_date'];

const initialState = {
  isLoading: true,
  setIsLoading: (isLoading) => {},
  search: '',
  setSearch: (search) => {},
  isMenuOpen: false,
  setIsMenuOpen: (isMenuOpen) => {},
  isLoggedIn: false,
  setIsLoggedIn: (isLoggedIn) => {},
  filters: {},
  setFilters: (filters) => {},
  dropdownOptions: {},
  setDropdownOptions: (dropdownOptions) => {},
  selectedService: {},
  setSelectedService: (service) => {},
  navItems: [],
  setNavItems: (items) => []
};

export const GlobalUtilContext = createContext(initialState);

function GlobalUtilProvider({ children }) {
  // Main global states we decide upon what is displayed
  const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem('auth'));
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  // Drawer items & state
  const [navItems, setNavItems] = useState([]);
  const [selectedDatasourceTab, setSelectedDatasourceTab] = useState(menuOptions[0]);
  const [selectedPartnerHistory, setSelectedPartnerHistory] = useState([]);

  // Utility states
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    single_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD').replaceAll('-', '/')
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [querySearch, setQuerySearch] = useSearchParams();

  const fetchAppData = useCallback(
    async (association_type) => {
      if (!navItems.length) {
        const datasources = await fetchDatasources();
        setNavItems(datasources);
      }

      if (!Object.keys(dropdownOptions).length) {
        const options = await fetchOptions();
        setDropdownOptions(options);
      }

      if (selectedService || association_type) {
        // Start async request for leaderboard
        setIsLoading(true);
        setIsLeaderboardLoading(true);
        fetchLeaderboard({
          ...filters,
          association_type: filters?.association_type || association_type
        }).then((leaderboard) => {
          setLeaderboard(leaderboard);
          setIsLeaderboardLoading(false);
        });

        // fetch batch analytics data synchronously as we need it to display most of the analytics at once
        const analytics = await fetchAnalytics({
          ...filters,
          association_type: filters?.association_type || association_type
        });
        setAnalyticsData(analytics);
        setIsLoading(false);
        return;
      } else {
        setIsLoading(false);
      }
    },
    [filters, isLoggedIn, selectedService, isLoading]
  );

  const handleSelectedService = useCallback(
    async (selectedService) => {
      setSelectedService(selectedService);
      setIsLoading(true);

      if (selectedService) {
        const history = await fetchPartnerHistory({
          association_type: selectedService.association_type
        });
        setSelectedPartnerHistory(history);
      }
      setFilters({
        ...filters,
        association_type: selectedService?.association_type || null
      });
    },
    [filters, setFilters, setSelectedPartnerHistory]
  );

  const handleSelectedPartner = useCallback(
    async (selectedPartner) => {
      setSelectedPartner(selectedPartner);
      setIsLoading(true);

      if (selectedPartner) {
        await addPartnerHistory({ log_partner_id: selectedPartner.id });
        const history = await fetchPartnerHistory({
          association_type: selectedService.association_type
        });
        setSelectedPartnerHistory(history);
        setSelectedDatasourceTab(menuOptions[1]);
      }
      setFilters({
        ...filters,
        partner: selectedPartner?.name_or_key || null
      });
    },
    [filters, setFilters, setSelectedPartnerHistory]
  );

  const queryToFilters = useCallback(async () => {
    const newFilters = {
      ...filters
    };
    queryFields.forEach((field) => {
      if (querySearch.get(field)) {
        newFilters[field] = querySearch.get(field);
      }
    });

    if (
      Object.keys(newFilters).includes('date_before') &&
      Object.keys(newFilters).includes('single_date')
    ) {
      newFilters.single_date = undefined;
    }

    const association_type = querySearch.get('association_type');
    const partner = querySearch.get('partner');

    // Populate datasource within app, get history for this datasource
    const datasources = await fetchDatasources();
    setNavItems(datasources);

    if (association_type) {
      const selectedService = datasources?.find(
        (source) => source.association_type === association_type
      );
      if (selectedService) {
        setSelectedService(selectedService);
      }

      const history = await fetchPartnerHistory({
        association_type: association_type
      });
      setSelectedPartnerHistory(history);
    }

    // Populate partner within app
    if (partner) {
      const fetchedPartner = await fetchPartnerLogSuggestions({
        association_type: association_type,
        search: partner
      });
      setSelectedPartner(fetchedPartner[0]);
      await addPartnerHistory({ log_partner_id: fetchedPartner[0].id });
      setSelectedDatasourceTab(menuOptions[1]);
    }
    setFilters(newFilters);
    setIsLoading(true);
    fetchAppData(association_type);
  }, [querySearch, filters, setSelectedPartner, setSelectedService]);

  const filtersToQuery = useCallback(
    (filters) => {
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) delete filters[key];
      });
      setQuerySearch(queryString.stringify(filters));
    },
    [setQuerySearch]
  );

  const resetFilters = useCallback(
    (currentFilters) => {
      const baseFilters = {
        ...filters
      };

      Object.keys(currentFilters).forEach((field) => {
        if (baseFiltersFields.includes(field)) return;

        delete baseFilters[field];
      });

      setFilters({
        ...baseFilters,
        single_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD').replaceAll('-', '/')
      });
    },
    [filters]
  );

  const resetDrawer = useCallback(() => {
    setFilters({
      ...filters,
      association_type: undefined,
      partner: undefined
    });
    setSelectedService(null);
    setSelectedPartner(null);
    setLeaderboard(null);
    setAnalyticsData(null);
    setSelectedPartnerHistory([]);
    setSelectedDatasourceTab(menuOptions[0]);
  }, [filters, setFilters]);

  useEffect(() => {
    if (isLoggedIn) queryToFilters();
  }, []);

  useEffect(() => {
    // Fetch data if filters change
    if (isLoggedIn) fetchAppData();

    // Set filters in queryParams
    filtersToQuery(filters);
  }, [filters, isLoggedIn]);

  return (
    <GlobalUtilContext.Provider
      value={{
        isLoading,
        setIsLoading,
        isLoggedIn,
        setIsLoggedIn,
        search,
        setSearch,
        isMenuOpen,
        setIsMenuOpen,
        filters,
        setFilters,
        dropdownOptions,
        setDropdownOptions,
        selectedService,
        handleSelectedService,
        navItems,
        setNavItems,
        selectedPartner,
        handleSelectedPartner,
        selectedPartnerHistory,
        setSelectedPartnerHistory,
        selectedDatasourceTab,
        setSelectedDatasourceTab,
        analyticsData,
        leaderboard,
        resetDrawer,
        resetFilters,
        isLeaderboardLoading
      }}
    >
      {children}
    </GlobalUtilContext.Provider>
  );
}

GlobalUtilProvider.propTypes = {
  children: PropTypes.any
};

export { GlobalUtilProvider };
